import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";

import * as React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import Footer from "../footer/Footer";
import ParentLoginDialog from "../../pages/ParentLogin";
import { verifyTokenApi } from "../../api/parentApi";

const drawerWidth = 260;

const navItems = [
  { title: "Home", path: "/" },
  { title: "About", path: "/about" },
  { title: "Contact Us", path: "/contact" },
  { title: "Pricing", path: "/pricing" },
];

function DrawerAppBar(props) {
  const { window: windowProp } = props;
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [loginAnchor, setLoginAnchor] = React.useState(null);
  const [profileAnchor, setProfileAnchor] = React.useState(null);
  const [parentInfo, setParentInfo] = React.useState(null);
  const [openLogin, setOpenLogin] = React.useState(false);

  const openLoginMenu = Boolean(loginAnchor);
  const openProfileMenu = Boolean(profileAnchor);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLoginMenuOpen = (event) => {
    setLoginAnchor(event.currentTarget);
  };

  const handleLoginMenuClose = () => {
    setLoginAnchor(null);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchor(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("parentInfo");
    localStorage.removeItem("parentToken");
    setParentInfo(null);
    handleProfileMenuClose();
  };

  React.useEffect(() => {
    const handleUrlToken = async () => {
      // Check for token in URL
      const params = new URLSearchParams(location.search);
      const urlToken = params.get("accessToken") || params.get("token");

      if (urlToken) {
        try {
          const result = await verifyTokenApi(urlToken);
          if (result?.meta?.success) {
            const parentData = result.response;
            localStorage.setItem("parentToken", parentData.token || urlToken);
            const pInfo = {
              name: parentData.GurardianName,
              email: parentData.GurardianEmailId,
              planName: parentData.planName || "Free Plan",
              planPrice: parentData.planPrice || 0,
            };
            localStorage.setItem("parentInfo", JSON.stringify(pInfo));
            setParentInfo(pInfo);

            // Clean the URL without refreshing
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (error) {
          console.error("Token verification from URL failed:", error);
        }
      } else {
        // Just rely on localStorage if no URL token is present
        const storedToken = localStorage.getItem("parentToken");
        const storedParent = localStorage.getItem("parentInfo");

        if (storedToken && storedParent) {
          setParentInfo(JSON.parse(storedParent));
        }
      }
    };

    handleUrlToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ✅ SAFE container handling
  const container =
    typeof windowProp === "function"
      ? () => windowProp().document.body
      : windowProp?.document?.body;

  const drawer = (
    <Box sx={{ textAlign: "center", mt: 3 }}>
      {/* Logo */}
      <Box
        component={Link}
        to="/"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
          textDecoration: "none",
          mb: 2,
        }}
      >
        <img src="/logo.png" alt="logo" style={{ height: 40 }} />

        <Typography fontWeight={700} color="#152259">
          MySchoolling
        </Typography>
      </Box>

      <Divider />

      <List>
        {navItems.map((item) => (
          <ListItem key={item.title} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemText primary={item.title} />
            </ListItemButton>
          </ListItem>
        ))}

        <Divider sx={{ my: 1 }} />

        {/* MOBILE AUTH MENU */}
        {!parentInfo ? (
          <>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  setMobileOpen(false);
                  setOpenLogin(true);
                }}
              >
                <ListItemText primary="Login as Parent" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                component="a"
                href="https://dev.admin.myschoolling.com"
                target="_blank"
              >
                <ListItemText primary="Login as School" />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <>
            <ListItem disablePadding>
              <ListItemText
                sx={{ px: 2, py: 1 }}
                primary={<Typography fontWeight={600}>{parentInfo.name}</Typography>}
                secondary={parentInfo.email}
              />
            </ListItem>
            <ListItem disablePadding>
              <ListItemText
                sx={{ px: 2, py: 0 }}
                primary={
                  <Typography variant="body2" color="primary">
                    Plan: {parentInfo.planName || "Free Plan"}
                  </Typography>
                }
              />
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  setMobileOpen(false);
                  handleLogout();
                }}
              >
                <ListItemText primary="Logout" sx={{ color: 'error.main' }} />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <CssBaseline />

      {/* APPBAR */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #eee",
        }}
      >
        <Toolbar
          sx={{
            height: 72,
            display: "flex",
            justifyContent: "space-between",
            maxWidth: 1280,
            width: "100%",
            margin: "0 auto",
          }}
        >
          {/* Mobile Menu */}
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              textDecoration: "none",
            }}
          >
            <img src="/logo.png" alt="logo" style={{ height: 36 }} />

            <Typography fontWeight={700} color="#152259">
              MySchoolling
            </Typography>
          </Box>

          {/* Desktop Menu */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: 4,
              alignItems: "center",
            }}
          >
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <Box
                  key={item.title}
                  component={Link}
                  to={item.path}
                  sx={{
                    textDecoration: "none",
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? "#152259" : "#555",
                    position: "relative",

                    "&:hover": { color: "#152259" },

                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: isActive ? "100%" : "0%",
                      height: "2px",
                      bottom: -6,
                      left: 0,
                      backgroundColor: "#152259",
                      transition: "width 0.3s",
                    },

                    "&:hover::after": { width: "100%" },
                  }}
                >
                  {item.title}
                </Box>
              );
            })}

            {/* LOGIN BUTTON */}
            {!parentInfo && (
              <>
                <Button
                  variant="contained"
                  onClick={handleLoginMenuOpen}
                  sx={{
                    backgroundColor: "#152259",
                    color: "#fff",
                    borderRadius: "30px",
                    px: 4,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Login
                </Button>

                <Menu
                  anchorEl={loginAnchor}
                  open={openLoginMenu}
                  onClose={handleLoginMenuClose}
                >
                  <MenuItem
                    onClick={() => {
                      handleLoginMenuClose();
                      setOpenLogin(true);
                    }}
                  >
                    Continue as Parent
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      handleLoginMenuClose();
                      window.open(
                        "https://dev.admin.myschoolling.com",
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                  >
                    Continue as School
                  </MenuItem>
                </Menu>
              </>
            )}

            {/* PROFILE */}
            {parentInfo && (
              <>
                <Avatar
                  onClick={handleProfileMenuOpen}
                  sx={{
                    width: 32,
                    height: 32,
                    border: "2px solid #152259",
                    bgcolor: "#152259",
                    cursor: "pointer",
                  }}
                >
                  {parentInfo.name?.charAt(0)}
                </Avatar>

                <Menu
                  anchorEl={profileAnchor}
                  open={openProfileMenu}
                  onClose={handleProfileMenuClose}
                >
                  <MenuItem disabled>
                    <Box>
                      <Typography fontWeight={600}>
                        {parentInfo.name}
                      </Typography>

                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {parentInfo.email}
                      </Typography>
                    </Box>
                  </MenuItem>

                  <Divider />

                  <MenuItem disabled>
                    <Typography variant="body2" color="primary" fontWeight={600}>
                      Plan: {parentInfo?.planName || "Free Plan"}
                    </Typography>
                  </MenuItem>

                  <Divider />

                  <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    Logout
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* MOBILE DRAWER */}
      <Drawer
        container={container}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>

      {/* MAIN CONTENT */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      {/* FOOTER */}
      <Footer />

      {/* PARENT LOGIN DIALOG */}
      <ParentLoginDialog
        open={openLogin}
        handleClose={() => {
          setOpenLogin(false);
          // Refresh user info after successful login
          const storedParent = localStorage.getItem("parentInfo");
          if (storedParent) {
            setParentInfo(JSON.parse(storedParent));
          }
        }}
      />
    </Box>
  );
}

export default DrawerAppBar;